import { useState, useCallback } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function CollapsibleJson({ title, data, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg">
      <button
        className="w-full flex items-center gap-2 p-2 text-left text-xs font-semibold text-muted-foreground hover:bg-muted/30"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {title}
      </button>
      {open && (
        <pre className="text-[10px] bg-muted/20 rounded-b p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function CreditNoteDiffPanel() {
  const [bookingId, setBookingId] = useState('9632652');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runDiff = useCallback(async () => {
    if (!bookingId.trim()) {
      toast.error('Booking ID is required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.creditNoteDiff(bookingId.trim());
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const topLevelDiff = result?.top_level_key_diff || [];
  const lineVatDiff = result?.line_vat_diff || [];
  const existingCount = result?.existing_credit_notes?.count ?? null;
  const reversalStrategy = result?.reversal_strategy;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          Credit Note Payload Diff
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only comparison of the credit-note builder output against a
          known-good accepted invoice. Identifies top-level keys and per-line
          vat_rate differences that could cause Pennylane NotAnyOf rejection.
          No writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 items-end">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Booking ID</label>
            <input
              type="text"
              value={bookingId}
              onChange={(e) => { setBookingId(e.target.value); setResult(null); }}
              className="block mt-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              placeholder="9632652"
            />
          </div>
          <Button onClick={runDiff} disabled={loading} variant="default">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run
          </Button>
        </div>

        {result?.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300">
            {result.error}
          </div>
        )}

        {result && !result.error && (
          <div className="space-y-4">
            {/* Summary badges */}
            <div className="flex flex-wrap gap-2">
              <Badge variant={existingCount === 0 ? 'outline' : 'destructive'}>
                Existing credit notes: {existingCount}
              </Badge>
              {topLevelDiff.length > 0 ? (
                <Badge variant="destructive">
                  Extra top-level keys: {topLevelDiff.join(', ')}
                </Badge>
              ) : (
                <Badge variant="outline">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  No extra top-level keys
                </Badge>
              )}
              {reversalStrategy && (
                <Badge variant={reversalStrategy === 'per_line' ? 'default' : 'outline'}>
                  Strategy: {reversalStrategy === 'per_line'
                    ? 'per-line reversal (mixed VAT rates)'
                    : 'single-line collapse (uniform rate)'}
                </Badge>
              )}
            </div>

            {/* Top-level key diff */}
            {topLevelDiff.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  Top-level keys in credit-note payload but absent from known-good create
                </div>
                <div className="flex flex-wrap gap-1">
                  {topLevelDiff.map((k) => (
                    <Badge key={k} variant="destructive" className="text-[10px] font-mono">{k}</Badge>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  These keys are not present in any accepted Pennylane create invoice payload.
                  The create schema rejects unexpected top-level properties with NotAnyOf.
                </p>
              </div>
            )}

            {/* Line VAT diff */}
            {lineVatDiff.length > 0 && (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Per-line VAT rate comparison</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">#</th>
                        <th className="py-1 pr-3 font-medium">Label</th>
                        <th className="py-1 pr-3 font-medium">Original vat_rate</th>
                        <th className="py-1 pr-3 font-medium">Builder vat_rate</th>
                        <th className="py-1 pr-3 font-medium">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineVatDiff.map((d) => (
                        <tr key={d.line_index} className="border-b border-border/50">
                          <td className="py-1 pr-3 font-mono">{d.line_index}</td>
                          <td className="py-1 pr-3">{d.label}</td>
                          <td className="py-1 pr-3 font-mono">{String(d.original_vat_rate)}</td>
                          <td className="py-1 pr-3 font-mono">{String(d.builder_vat_rate)}</td>
                          <td className="py-1 pr-3">
                            {d.match ? (
                              <CheckCircle2 className="h-3 w-3 text-green-600 inline" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 text-amber-500 inline" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Collapsible JSON blocks */}
            <div className="space-y-2">
              <CollapsibleJson
                title={`Existing credit notes (${existingCount})`}
                data={result.existing_credit_notes}
              />
              <CollapsibleJson
                title="Original invoice (resolved lines)"
                data={result.original_invoice}
              />
              <CollapsibleJson
                title="Known-good accepted invoice"
                data={result.known_good_invoice}
              />
              <CollapsibleJson
                title="Credit-note builder output"
                data={result.credit_note_builder_output}
              />
              {result.credit_note_payload && (
                <CollapsibleJson
                  title="Full credit-note POST payload (as would be sent)"
                  data={result.credit_note_payload}
                  defaultOpen
                />
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
